import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { createWidgetInstanceRegistry } from './createWidgetInstanceRegistry';
import type { WidgetInstanceRegistry } from './types';

const WidgetInstanceRegistryContext =
  createContext<WidgetInstanceRegistry | null>(null);

const WidgetInstanceRegistryVersionContext = createContext(0);

export interface WidgetInstanceRegistryProviderProps {
  readonly children: ReactNode;
}

export function WidgetInstanceRegistryProvider({
  children,
}: WidgetInstanceRegistryProviderProps) {
  const [version, setVersion] = useState(0);
  const registryRef = useRef<WidgetInstanceRegistry | null>(null);

  const bumpVersion = useCallback(() => {
    setVersion((value) => value + 1);
  }, []);

  if (!registryRef.current) {
    registryRef.current = createWidgetInstanceRegistry(bumpVersion);
  }

  return (
    <WidgetInstanceRegistryContext.Provider value={registryRef.current}>
      <WidgetInstanceRegistryVersionContext.Provider value={version}>
        {children}
      </WidgetInstanceRegistryVersionContext.Provider>
    </WidgetInstanceRegistryContext.Provider>
  );
}

export function useWidgetInstanceRegistry(): WidgetInstanceRegistry {
  const registry = useContext(WidgetInstanceRegistryContext);
  if (!registry) {
    throw new Error(
      'useWidgetInstanceRegistry must be used within WidgetInstanceRegistryProvider'
    );
  }
  return registry;
}

export function useWidgetInstanceRegistryVersion(): number {
  return useContext(WidgetInstanceRegistryVersionContext);
}

export function useOptionalWidgetInstanceRegistry():
  | WidgetInstanceRegistry
  | undefined {
  return useContext(WidgetInstanceRegistryContext) ?? undefined;
}
